import axios from 'axios'
import { useLayoutEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux'
import numberOfAttendeesIcon from '../../../../assets/icons/numberOfAttendees.svg'
import pricingIcon from '../../../../assets/icons/dollar.svg'
import discountIcon from '../../../../assets/icons/discount.svg'
import {
  calculateIvyLeagueTotalsFromAdminConfiguration,
  getIvyLeagueCreditCardSurchargeRate,
  getIvyLeagueHybridValidationErrors,
  formatNumberWithCommas
} from '../../../../helpers/ivyLeague/helpers'
import { config } from '../../../../config'

// Keep a cross-mount (step back/forward) record of what we've already logged
// for this browser session, to avoid duplicate rows when Step4 unmounts.
const sentSignatures = new Set<string>()

// Admin-configured text may contain literal "\n" or real newline characters; render both as line breaks.
const renderWithLineBreaks = (text?: string) => {
  if (!text) return null

  return text.split(/\\n|\n/).map((line, index, lines) => (
    <span key={index}>
      {line}
      {index < lines.length - 1 && <br />}
    </span>
  ))
}

export const Step4 = () => {
  const { kids, familyInfo, adminConfiguration } = useSelector(
    (state: RootState) => state.ivyLeague
  )
  const sentSignaturesRef = useRef<Set<string>>(sentSignatures)

  const { childBreakdowns, familyBreakdown } = useMemo(
    () => calculateIvyLeagueTotalsFromAdminConfiguration(kids, familyInfo, adminConfiguration),
    [kids, familyInfo, adminConfiguration]
  )

  const tuitionTotal = familyBreakdown.familyGross
  const totalDiscounts = useMemo(() => {
    const perChildDiscounts = (childBreakdowns ?? []).reduce((sum, b) => {
      return sum +
        (b.teacherDiscount ?? 0) +
        (b.occupationalDiscount ?? 0) +
        (b.transportationDiscount ?? 0) +
        (b.siblingDiscount ?? 0) +
        (b.alumniDiscount ?? 0) +
        (b.paymentDiscount ?? 0)
    }, 0)

    return perChildDiscounts + (familyBreakdown.referralDiscountAmount ?? 0)
  }, [childBreakdowns, familyBreakdown.referralDiscountAmount])

  // Use the same total that we log to the spreadsheet (includes any CC surcharge, etc.)
  const grandTotal = familyBreakdown.finalAmountDue ?? familyBreakdown.familySubtotalAfterReferral ?? 0

  const creditCardFee = familyInfo.paymentMethod === 'CreditCard'
    ? (familyBreakdown.creditCardFee ?? 0)
    : 0

  const creditCardSurchargeRate = useMemo(() => {
    return getIvyLeagueCreditCardSurchargeRate(adminConfiguration)
  }, [adminConfiguration])

  const creditCardSurchargeRateLabel = useMemo(() => {
    const pct = creditCardSurchargeRate * 100
    // Display like 3.5% or 4%.
    const pretty = Number.isFinite(pct) ? pct.toFixed(1).replace(/\.0$/, '') : '0'
    return `${pretty}%`
  }, [creditCardSurchargeRate])

  const discountPercentage = tuitionTotal > 0
    ? Math.round((totalDiscounts / tuitionTotal) * 100)
    : 0

  const addOnsTotal = useMemo(() => {
    return (childBreakdowns ?? []).reduce((sum, b) => {
      const transportationFee = (b.transportationDiscount ?? 0) < 0
        ? Math.abs(b.transportationDiscount ?? 0)
        : 0

      return sum +
        (b.busFee ?? 0) +
        (b.finaleFee ?? 0) +
        transportationFee
    }, 0)
  }, [childBreakdowns])

  const spreadsheetValidationErrors = useMemo(() => {
    const errors: string[] = []
    if (!familyInfo.firstName?.trim()) errors.push('Missing parent first name')
    if (!familyInfo.lastName?.trim()) errors.push('Missing parent last name')
    if (!familyInfo.email?.trim()) errors.push('Missing parent email')
    if ((kids ?? []).length === 0) errors.push('No campers')

    ;(kids ?? []).slice(0, 5).forEach((k, idx) => {
      if (!k.grade?.trim()) errors.push(`C${idx + 1} missing grade`)
      const hasAnySession = (k.programs ?? []).some((p) => (p.programType ?? '').trim() && (p.programName ?? '').trim())
      if (!hasAnySession) errors.push(`C${idx + 1} missing sessions`)

      const hybridErrors = getIvyLeagueHybridValidationErrors(k.grade, k.programs ?? [])
      hybridErrors.forEach((msg) => errors.push(`C${idx + 1} ${msg}`))
    })

    return errors
  }, [kids, familyInfo.firstName, familyInfo.lastName, familyInfo.email])

  const spreadsheetRowsBase = useMemo(() => {
    const formatSessions = (kidIndex: number): string => {
      const kid = kids?.[kidIndex]
      if (!kid) return ''
      const programs = kid.programs ?? []
      if (!programs.length) return ''

      return programs
        .map((p) => {
          const type = (p.programType ?? '').trim() || 'Unknown'
          const name = (p.programName ?? '').trim() || 'Unknown'
          const weeks = typeof p.weekCount === 'number' ? p.weekCount : 0
          return `${type} - ${name} (${weeks}w)`
        })
        .join(', ')
    }

    return [
      familyInfo.firstName ?? '',
      familyInfo.lastName ?? '',
      familyInfo.email ?? '',
      familyInfo.phone ?? '',
      (kids ?? []).length,
      tuitionTotal ?? 0,
      totalDiscounts ?? 0,
      // Add-ons are excluded from discount % but included in the grand total.
      addOnsTotal ?? 0,
      discountPercentage ?? 0,
      grandTotal ?? 0,
      kids?.[0]?.grade ?? '',
      formatSessions(0),
      kids?.[1]?.grade ?? '',
      formatSessions(1),
      kids?.[2]?.grade ?? '',
      formatSessions(2),
      kids?.[3]?.grade ?? '',
      formatSessions(3),
      kids?.[4]?.grade ?? '',
      formatSessions(4),
      spreadsheetValidationErrors.join(', ')
    ]
  }, [
    kids,
    familyInfo.firstName,
    familyInfo.lastName,
    familyInfo.email,
    familyInfo.phone,
    tuitionTotal,
    totalDiscounts,
    addOnsTotal,
    discountPercentage,
    grandTotal,
    spreadsheetValidationErrors
  ])

  // Signature is based on the spreadsheet payload (excluding timestamp) to avoid duplicate logging.
  const payloadSignature = useMemo(() => {
    return JSON.stringify({
      sheetName: 'Tuition Calculator Logs',
      rowsBase: spreadsheetRowsBase
    })
  }, [spreadsheetRowsBase])

  useLayoutEffect(() => {
    if (sentSignaturesRef.current.has(payloadSignature)) return
    sentSignaturesRef.current.add(payloadSignature)

    if (spreadsheetValidationErrors.length > 0) {
      return
    }

    const timeStamp = new Date().toISOString()

    const payload = {
      sheetName: 'Tuition Calculator Logs',
      rows: [
        timeStamp,
        ...spreadsheetRowsBase
      ]
    }

    // Fire-and-forget: do not cancel on unmount (users may go back a step quickly).
    // Always catch so we don't surface an unhandled promise rejection.
    axios
      .post(
      config.ivyLeagueGenieLogsApiUrl,
      new URLSearchParams({
        payload: JSON.stringify(payload)
      }),
      {
        params: {
          apiKey: config.ivyLeagueApiKey ?? ''
        }
      }
      )
      .catch(() => {
        // Intentionally ignore logging failures.
      })
  }, [
    payloadSignature,
    spreadsheetRowsBase,
    spreadsheetValidationErrors.length
  ])

  return (
    <div className="step4">
      <div className="summaryDetailsArea">
        <h3 className="summaryTitle">Summary</h3>
        <div className="familyInfoCard">
          <h3 className="cardTitle">Guardian&apos;s Information</h3>
          <div className="row">
            <label className="fieldLabel">
              <span>#</span>Name: {familyInfo.firstName} {familyInfo.lastName}
            </label>
          </div>
          <div className="row">
            <label className="fieldLabel">
              <span>#</span>Email: {familyInfo.email}
            </label>
          </div>
          <div className="row">
            <label className="fieldLabel">
              <span>#</span>Phone: {familyInfo.phone}
            </label>
          </div>
        </div>
        <div className="numberOfAttendees">
          <img src={numberOfAttendeesIcon} className="icon" alt="" />
          <label className="fieldLabel">{kids.length} Child(ren)</label>
        </div>
        <div className="sessionsDetailsArea">
          {kids.map((kid) => {
            const breakdown = childBreakdowns.find((b) => b.childId === kid.id)
            if (!breakdown) return null

            const totalWeeks = (kid.programs ?? []).reduce((s, p) => s + (p.weekCount ?? 0), 0)
            const weeksOnCampus = (kid.programs ?? [])
              .filter((p) => p.programType === 'OnCampus')
              .reduce((s, p) => s + (p.weekCount ?? 0), 0)
            const weeksOffCampus = (kid.programs ?? [])
              .filter((p) => p.programType === 'OffCampus')
              .reduce((s, p) => s + (p.weekCount ?? 0), 0)

            return (
              <div
                className="attendeeDetailCard"
                key={kid.id}
              >
                <h3 className="cardTitle">{kid.id}</h3>
                <div className="mainRow">
                  <div className="column1">
                    <div className="row">
                      <label className="fieldLabel">
                        <span>#</span>Grade: {kid.grade}
                      </label>
                    </div>
                    <div className="row">
                      <label className="fieldLabel">
                        <span>#</span>Programs: {(kid.programs ?? []).map((p) => `${p.programType} - ${p.programName}`).join(', ')}
                      </label>
                    </div>
                    <div className="row">
                      <label className="fieldLabel">
                        <span>#</span>Weeks: {totalWeeks} total ({weeksOnCampus} on-campus, {weeksOffCampus} off-campus)
                      </label>
                    </div>
                    <div className="row">
                      <label className="fieldLabel">
                        <span>#</span>Transport: {kid.transportMode ?? 'Parent'}
                      </label>
                    </div>
                    {familyInfo.hasReferralDiscount && (
                      <div className="row">
                        <label className="fieldLabel">
                          <span>#</span>Referral: -${formatNumberWithCommas(breakdown.referralDiscount ?? 0)}
                        </label>
                      </div>
                    )}
                    {breakdown.teacherDiscount > 0 && (
                      <div className="row">
                        <label className="fieldLabel">
                          <span>#</span>Teacher Family
                        </label>
                      </div>
                    )}
                    {breakdown.occupationalDiscount > 0 && (
                      <div className="row">
                        <label className="fieldLabel">
                          <span>#</span>Military
                        </label>
                      </div>
                    )}
                    {breakdown.alumniDiscount > 0 && (
                      <div className="row">
                        <label className="fieldLabel">
                          <span>#</span>Alumni
                        </label>
                      </div>
                    )}
                    {breakdown.siblingDiscount > 0 && (
                      <div className="row">
                        <label className="fieldLabel">
                          <span>#</span>Sibling
                        </label>
                      </div>
                    )}
                    {breakdown.finaleFee > 0 && (
                      <div className="row">
                        <label className="fieldLabel">
                          <span>#</span>Finale Trip
                        </label>
                      </div>
                    )}
                  </div>
                  {process.env.NODE_ENV === 'development' && (
                    <div className="column2">
                      <div className="row">
                        <label className="fieldLabel">Gross: ${formatNumberWithCommas(breakdown.grossTuition)}</label>
                      </div>
                      {familyInfo.hasReferralDiscount && (breakdown.referralDiscount ?? 0) > 0 && (
                        <div className="row subRow">
                          <label className="fieldLabel">- Referral: ${formatNumberWithCommas(breakdown.referralDiscount)}</label>
                        </div>
                      )}
                      {breakdown.transportationDiscount !== 0 && (
                        <div className="row subRow">
                          {breakdown.transportationDiscount > 0
                            ? (
                              <label className="fieldLabel">- Not using transportation: ${formatNumberWithCommas(breakdown.transportationDiscount)}</label>
                            )
                            : (
                              <label className="fieldLabel">+ Transportation fee: ${formatNumberWithCommas(Math.abs(breakdown.transportationDiscount))}</label>
                            )}
                        </div>
                      )}
                      {breakdown.alumniDiscount > 0 && (
                        <div className="row subRow">
                          <label className="fieldLabel">- Alumni: ${formatNumberWithCommas(breakdown.alumniDiscount)}</label>
                        </div>
                      )}
                      {breakdown.siblingDiscount > 0 && (
                        <div className="row subRow">
                          <label className="fieldLabel">- Sibling: ${formatNumberWithCommas(breakdown.siblingDiscount)}</label>
                        </div>
                      )}
                      {breakdown.teacherDiscount > 0 && (
                        <div className="row subRow">
                          <label className="fieldLabel">- Teacher: ${formatNumberWithCommas(breakdown.teacherDiscount)}</label>
                        </div>
                      )}
                      {breakdown.occupationalDiscount > 0 && (
                        <div className="row subRow">
                          <label className="fieldLabel">- Military: ${formatNumberWithCommas(breakdown.occupationalDiscount)}</label>
                        </div>
                      )}
                      {breakdown.paymentDiscount > 0 && (
                        <div className="row subRow">
                          <label className="fieldLabel">- Payment: ${formatNumberWithCommas(breakdown.paymentDiscount)}</label>
                        </div>
                      )}
                      {breakdown.busFee > 0 && (
                        <div className="row subRow">
                          <label className="fieldLabel">+ Bus fee: ${formatNumberWithCommas(breakdown.busFee)}</label>
                        </div>
                      )}
                      {breakdown.finaleFee > 0 && (
                        <div className="row subRow">
                          <label className="fieldLabel">+ Finale trip: ${formatNumberWithCommas(breakdown.finaleFee)}</label>
                        </div>
                      )}
                      <div className="row">
                        <label className="fieldLabel">
                          <strong>Subtotal: ${formatNumberWithCommas(breakdown.childSubtotal)}</strong>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <div className="summaryAmountArea">
        <h3 className="pricingBreakDownTitle">Pricing Summary</h3>
        <div className="pricingColumn">
          <div className="pricingRow">
            <label className="fieldLabel">Tuition Total</label>
            <div className="price">
              <img src={pricingIcon} className="pricingIcon" alt="" />
              <h3 className="subPrice">${formatNumberWithCommas(tuitionTotal)}</h3>
            </div>
          </div>
          <div className="discountRow">
            <label className="fieldLabel">Total Discounts</label>
            <div className="price">
              <img src={discountIcon} className="discountIcon" alt="" />
              <h3 className="subPrice">${formatNumberWithCommas(totalDiscounts)} ({discountPercentage}%)</h3>
            </div>
          </div>
          {creditCardFee > 0 && (
            <div className="discountRow">
              <label className="fieldLabel">Credit Card Surcharge ({creditCardSurchargeRateLabel})</label>
              <div className="price">
                <img src={pricingIcon} className="discountIcon" alt="" />
                <h3 className="subPrice">{`+$${formatNumberWithCommas(creditCardFee)}`}</h3>
              </div>
            </div>
          )}
          <div className="pricingRow">
            <label className="fieldLabel">Grand Total</label>
            <div className="price">
              <img src={pricingIcon} className="pricingIcon" alt="" />
              <h3 className="subPrice">${formatNumberWithCommas(grandTotal)}</h3>
            </div>
          </div>
        </div>
        <div className='additionalText'>
          <p style={{ marginBottom: '10px' }}>
            {renderWithLineBreaks(adminConfiguration?.dynamicContent['Genie Description'])}
          </p>
          <p>*{adminConfiguration?.dynamicContent['Quoted Price Validity']}</p>
        </div>
      </div>
    </div>
  )
}
